import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, createContext } from 'react';
import { ToastProvider } from './components/ToastProvider';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ClientsPage from './pages/ClientsPage';
import TeamsPage from './pages/TeamsPage';
import TimeEntriesPage from './pages/TimeEntriesPage';
import ReportsPage from './pages/ReportsPage';
import TimesheetsPage from './pages/TimesheetsPage';
import BudgetsPage from './pages/BudgetsPage';
import TemplatesPage from './pages/TemplatesPage';
import AdminPage from './pages/AdminPage';
import PlannedHoursPage from './pages/PlannedHoursPage';
import SetPasswordPage from './pages/SetPasswordPage';
import { PermissionProvider } from '@/contexts/PermissionContext';
import { NetworkProvider } from '@/contexts/NetworkContext';
import { PermissionGuard } from '@/components/PermissionGuard';
import { api, getToken } from './services/api';
import type { User } from './types';

interface AuthContextType {
  user: User | null;
  setUser: (u: User | null) => void;
  isLoading: boolean;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  isLoading: true,
  logout: () => {},
});

function AppContent() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setIsLoading(false);
      return;
    }
    api<User>('/auth/me')
      .then((u) => setUser(u))
      .catch(() => {/* silently fail */})
      .finally(() => setIsLoading(false));
  }, []);

  const logout = () => {
    api('/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)]">
        <div className="relative mb-8 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
          <div className="absolute inset-0 h-14 w-14 animate-ping rounded-xl bg-[var(--color-primary)] opacity-20" />
        </div>
        <div className="text-2xl font-bold tracking-tight text-[var(--color-foreground)]">Jani</div>
        <div className="mt-4 h-1.5 w-48 overflow-hidden rounded-full bg-[var(--color-slate-200)]">
          <div className="h-full animate-[skeleton-shimmer_1.5s_infinite] rounded-full bg-[var(--color-primary)]" style={{ width: '40%' }} />
        </div>
      </div>
    );
  }

  return (
    <>
      {user?.impersonatorId && (
        <div className="sticky top-0 z-50 bg-amber-50 px-4 py-2 text-center text-sm font-medium text-amber-900 border-b border-amber-200">
          You are viewing as <strong>{user.name}</strong>.
          <button
            onClick={() => { localStorage.removeItem('jani_impersonate_user_id'); window.location.reload(); }}
            className="ml-2 underline underline-offset-2 hover:text-amber-700"
          >
            Return to admin
          </button>
        </div>
      )}
      <AuthContext.Provider value={{ user, setUser, isLoading, logout }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
            <Route path="/register" element={user ? <Navigate to="/" /> : <RegisterPage />} />
            <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
            <Route path="/reset-password" element={user ? <Navigate to="/" /> : <ResetPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/*" element={user ? (
              <PermissionProvider>
                <Layout><InnerRoutes /></Layout>
              </PermissionProvider>
            ) : <Navigate to="/login" />} />
          </Routes>
        </BrowserRouter>
      </AuthContext.Provider>
    </>
  );
}

function InnerRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/time-entries" element={<PermissionGuard pageKey="time-entries" minLevel="VIEWOWN"><TimeEntriesPage /></PermissionGuard>} />
      <Route path="/timesheets" element={<PermissionGuard pageKey="timesheets" minLevel="VIEWOWN"><TimesheetsPage /></PermissionGuard>} />
      <Route path="/planning" element={<PermissionGuard pageKey="planned-hours" minLevel="VIEWOWN"><PlannedHoursPage /></PermissionGuard>} />
      <Route path="/projects" element={<PermissionGuard pageKey="projects" minLevel="VIEWOWN"><ProjectsPage /></PermissionGuard>} />
      <Route path="/projects/:id" element={<PermissionGuard pageKey="projects" minLevel="VIEWOWN"><ProjectDetailPage /></PermissionGuard>} />
      <Route path="/clients" element={<PermissionGuard pageKey="clients" minLevel="VIEWOWN"><ClientsPage /></PermissionGuard>} />
      <Route path="/teams" element={<PermissionGuard pageKey="teams" minLevel="VIEWOWN"><TeamsPage /></PermissionGuard>} />
      <Route path="/reports" element={<PermissionGuard pageKey="reports" minLevel="VIEWOWN"><ReportsPage /></PermissionGuard>} />
      <Route path="/budgets" element={<PermissionGuard pageKey="budgets" minLevel="VIEWOWN"><BudgetsPage /></PermissionGuard>} />
      <Route path="/admin" element={<PermissionGuard pageKey="admin" minLevel="READ"><AdminPage /></PermissionGuard>} />
      <Route path="/templates" element={<PermissionGuard pageKey="templates" minLevel="READ"><TemplatesPage /></PermissionGuard>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <NetworkProvider>
        <AppContent />
      </NetworkProvider>
    </ToastProvider>
  );
}
